import type { ReactNode } from "react";
import { buildManualTableBusRowsForGroup, buildManualTableGroupDisplay, buildManualTableLineDisplay, isLegacyBusFuelBasisText, manualTableMoney, type ManualTableGroupDisplay, type ManualTableLineDisplay } from "../service/quoteBudgetManualTableService";
import { formatMoney, formatLineGroupTotalForDisplay } from "../service/quoteBudgetEditorViewService";
import { buildRolePerCapitaSummary, type QuoteBudgetWorkspaceGroup, type WorkspaceCategory } from "../service/quoteBudgetWorkspaceService";
import type { QuoteBudgetLine, QuoteBudgetPeopleCounts } from "../types";
import type { ManualEntryContext } from "../types/manualEntry";
import "./quoteBudgetManualTable.css";

type Column = { title: ReactNode; width: number; numeric?: boolean };
const COLUMNS: Partial<Record<WorkspaceCategory, Column[]>> = {
  stay: [
    { title: "住宿区域", width: 15 }, { title: "住宿标准", width: 13 }, { title: "角色", width: 6 },
    { title: "入住人数", width: 10, numeric: true }, { title: "晚数", width: 6, numeric: true },
    { title: <>单价<small>元/人/晚</small></>, width: 11, numeric: true }, { title: "金额", width: 13, numeric: true },
    { title: "区域小计", width: 13, numeric: true }, { title: "操作", width: 13, numeric: true },
  ],
  meal: [
    { title: "用餐区域", width: 14 }, { title: "类别", width: 8 }, { title: "角色", width: 7 },
    { title: "用餐人数", width: 12, numeric: true }, { title: "餐数", width: 7, numeric: true },
    { title: <>餐标<small>元/人/餐</small></>, width: 12, numeric: true }, { title: "金额", width: 14, numeric: true },
    { title: "区域小计", width: 14, numeric: true }, { title: "操作", width: 12, numeric: true },
  ],
  air: [
    { title: "城市路线", width: 23 }, { title: "费用项目", width: 10 }, { title: "角色", width: 8 },
    { title: "人数", width: 13, numeric: true }, { title: "单价", width: 11, numeric: true },
    { title: "金额", width: 12, numeric: true }, { title: "路线小计", width: 12, numeric: true }, { title: "操作", width: 11, numeric: true },
  ],
  bus: [
    { title: "区域 / 行程范围", width: 25 }, { title: "车型", width: 11 }, { title: "辆数", width: 7, numeric: true },
    { title: "整段单车价", width: 15, numeric: true }, { title: "金额", width: 14, numeric: true },
    { title: "本组小计", width: 15, numeric: true }, { title: "操作", width: 13, numeric: true },
  ],
  point: [
    { title: "考察点", width: 15 }, { title: "SKU 名称", width: 17 }, { title: "角色", width: 6 },
    { title: "人数", width: 6, numeric: true }, { title: "次数", width: 6, numeric: true },
    { title: "单价", width: 10, numeric: true }, { title: "金额", width: 11, numeric: true },
    { title: "本点小计", width: 11, numeric: true }, { title: "操作", width: 18, numeric: true },
  ],
  crew: [
    { title: "费用项目", width: 19 }, { title: "角色", width: 9 }, { title: "计费人数", width: 16, numeric: true },
    { title: "天数 / 晚数 / 次数", width: 15, numeric: true }, { title: "单价", width: 14, numeric: true },
    { title: "金额", width: 15, numeric: true }, { title: "操作", width: 12, numeric: true },
  ],
  misc: [
    { title: "费用名称", width: 16 }, { title: "计费对象", width: 12 },
    { title: "计算依据", width: 22 }, { title: "金额", width: 12, numeric: true },
    { title: "本组小计", width: 12, numeric: true }, { title: "操作", width: 11, numeric: true },
  ],
};
const CATEGORY_NAMES: Record<WorkspaceCategory, string> = { stay: "住宿", rail: "火车", air: "飞机", ship: "船", bus: "大巴车", point: "门票", meal: "餐", crew: "司导", misc: "杂费" };

/** 分类表仅消费工作态明细和操作出口，不保存或重算金额。 */
export type QuoteBudgetManualTableProps = {
  groups: QuoteBudgetWorkspaceGroup[];
  category?: WorkspaceCategory;
  people: QuoteBudgetPeopleCounts;
  teacherPays?: boolean;
  areas?: ManualEntryContext["areas"];
  itineraryDays?: ManualEntryContext["itineraryDays"];
  collapsed: Record<string, boolean>;
  showActions: boolean;
  showPriceBasis?: boolean;
  renderGroup: (group: QuoteBudgetWorkspaceGroup, display?: ManualTableGroupDisplay) => ReactNode;
  renderActions: (line: QuoteBudgetLine, rowKey: string, display: ManualTableLineDisplay) => ReactNode;
  riskReasonByLineId?: Record<string, string>;
  locatedLineId?: string | null;
};

/** 费用身份与风险保持在同一单元格，兼容行也不丢失提醒。 */
function LineName({ line, risk, children }: { line: QuoteBudgetLine; risk?: string; children?: ReactNode }) {
  return <>{line.skuNameSnapshot || line.itemNameSnapshot || line.resourceEntryType}{children}{risk ? <small className="qb-warning">{risk}</small> : null}</>;
}

/** 与正式成本分析共用精确分摊，缺价只计已知费用并保留提示。 */
function GroupPerCapita({ lines, people, teacherPays = false }: { lines: QuoteBudgetLine[]; people: QuoteBudgetPeopleCounts; teacherPays?: boolean }) {
  const payers = buildRolePerCapitaSummary(lines, people, teacherPays).filter(item => item.status === "priced" || item.status === "pending");
  if (!payers.length) return <>—</>;
  return <>{payers.map(item => <span key={item.role} title={`${item.role}承担的人均费用`}>
    {teacherPays ? <small>{item.role}</small> : null}
    {item.amount !== null ? `${manualTableMoney(formatMoney(item.amount))}/人` : "待补价"}
    {item.status === "pending" && item.amount !== null ? <small>待补价</small> : null}
  </span>)}</>;
}

/** 各类沿用原型列；火车与飞机共用城市路线列，风险模式按类逐表展示。 */
export function QuoteBudgetManualTable(props: QuoteBudgetManualTableProps) {
  const categories = new Map<WorkspaceCategory, QuoteBudgetWorkspaceGroup[]>();
  if (props.category && props.groups.length === 0) categories.set(props.category, []);
  for (const group of props.groups) categories.set(group.category, [...(categories.get(group.category) ?? []), group]);
  const sections = [...categories].flatMap(([category, groups]) => category === "misc" && groups.length
    ? groups.map(group => ({ category, groups: [group], key: group.id, title: group.label }))
    : [{ category, groups, key: category, title: categories.size > 1 ? CATEGORY_NAMES[category] : null }]);
  return <>{sections.map(({ category, groups, key, title }) => {
    const flat = category === "crew";
    const hasGroupColumn = !flat && category !== "misc";
    const columns = COLUMNS[category === "ship" || category === "rail" ? "air" : category]!;
    const withPerCapita = [...columns.slice(0, -1), { title: "人均均摊", width: 12, numeric: true }, columns[columns.length - 1]];
    const visibleColumns = props.showActions ? withPerCapita : withPerCapita.slice(0, -1);
    const totalWidth = visibleColumns.reduce((sum, column) => sum + column.width, 0);
    const shownColumns = visibleColumns.map(column => ({ ...column, width: category === "misc" ? 100 / visibleColumns.length : column.width / totalWidth * 100 }));
    return <section className={`qb-category-table-section qb-category-table-section--${category}`} key={key} aria-label={`${CATEGORY_NAMES[category]}${category === "misc" && title ? title : "费用"}明细`}>
      {title ? <h3 className="qb-category-table-title">{title}</h3> : null}
      {flat ? groups.map(group => <div className="qb-category-table-group-tools" key={group.id}>{props.renderGroup(group)}</div>) : null}
      <div className="qb-category-table-scroll">
        <table className={`qb-category-table qb-category-table--${category}`}>
          <colgroup>{shownColumns.map((column, index) => <col key={index} style={{ width: `${column.width}%` }} />)}</colgroup>
          <thead><tr>{shownColumns.map((column, index) => <th key={index} scope="col" className={column.numeric ? "number" : undefined}>{column.title}</th>)}</tr></thead>
          {groups.map(group => {
            const sourceLines = category !== "misc" && props.collapsed[group.id] ? group.lines.slice(0, 1) : group.lines;
            const rows = category === "bus"
              ? buildManualTableBusRowsForGroup(sourceLines, props.people)
              : sourceLines.map((line) => ({
                key: line.budgetLineId,
                line,
                display: buildManualTableLineDisplay(line, props.people),
                showActions: true,
              }));
            const groupDisplay = buildManualTableGroupDisplay(group, { areas: props.areas, itineraryDays: props.itineraryDays });
            return <tbody key={group.id}>{rows.map((row, index) => {
              const { line, display } = row;
              const risk = props.riskReasonByLineId?.[line.budgetLineId];
              const amount = props.showPriceBasis && line.priceSourceType === "unpriced" ? "待补价" : display.amount;
              // 主表不再展示「随人数 / 固定人数」模式小字，人数列只保留数值。
              const modeNote = display.mode === "随人数" || display.mode === "固定人数" ? null
                : <small className="qb-category-table-mode">{display.mode}</small>;
              const price = <td className="number">{display.price ?? "—"}</td>;
              const personCount = <td className="number">{display.people ?? "—"}{display.people === null ? null : modeNote}</td>;
              let detail: ReactNode;
              if (category === "misc") {
                const total = line.entrySnapshot?.quantityMode === "total" || line.unit === "项";
                detail = <><td><LineName line={line} risk={risk} /></td><td>{total ? "整项" : line.travelerRole || "指定人数"}</td><td className="qb-category-table-formula">{display.formula}{display.compatibility && line.priceBasisDesc && !props.showPriceBasis ? <small>{line.priceBasisDesc}</small> : null}</td></>;
              } else if (display.compatibility) {
                const basis = line.priceBasisDesc && !isLegacyBusFuelBasisText(line.priceBasisDesc) ? line.priceBasisDesc : null;
                detail = <td colSpan={columns.length - (flat ? 2 : 4)} className="qb-category-table-legacy"><LineName line={line} risk={risk} /><small>{line.travelerRole || "原有费用"} · 原计费依据</small><span>{display.formula}</span>{basis ? <small>{basis}</small> : null}</td>;
              } else if (category === "bus") {
                detail = <><td>{display.vehicle ?? <LineName line={line} risk={risk} />}{display.vehicle && risk && row.showActions ? <small className="qb-warning">{risk}</small> : null}</td><td className="number">{display.factor ?? line.quantity}</td>{price}</>;
              } else {
                const entryName = category === "meal" ? line.mealType || undefined
                  : category === "air" ? line.resourceEntryType === "手续费" ? "手续费" : "机票"
                    : category === "rail" ? line.entrySnapshot?.railFeeKind ? line.itemNameSnapshot : line.resourceEntryType === "手续费" ? "手续费" : line.itemSpecSnapshot === "铁路票价" ? line.itemNameSnapshot : "火车票"
                      : category === "ship" ? "船票" : undefined;
                detail = <><td>{entryName ? <>{entryName}{risk ? <small className="qb-warning">{risk}</small> : null}{line.resourceEntryType === "手续费" ? <small>{display.factor}次</small> : null}</> : <LineName line={line} risk={risk}>{category === "point" ? <small>{display.mode === "按场次" ? "按场次" : "按人数"}</small> : null}</LineName>}</td>
                  <td>{line.travelerRole || "—"}</td>{personCount}
                  {category !== "air" && category !== "ship" && category !== "rail" ? <td className="number">{display.factorUnit === "" ? "—" : display.factor ?? "—"}{category === "crew" ? display.factorUnit : (category === "point" && display.factorUnit === "场") || (category === "meal" && display.factorUnit === "桌") ? display.factorUnit : ""}</td> : null}
                  {category === "crew" ? <td className="number">{display.price}<small>{line.unit ? `元/${line.unit.replace(/^人(.)$/, "人/$1")}` : ""}</small></td> : price}</>;
              }
              return <tr key={row.key} data-budget-line-id={line.budgetLineId} tabIndex={-1} aria-current={props.locatedLineId === line.budgetLineId ? "true" : undefined}>
                {hasGroupColumn && index === 0 ? <td rowSpan={rows.length} className="qb-category-table-group">{props.renderGroup(group, category === "bus" ? groupDisplay : undefined)}{category === "bus" ? null : groupDisplay.notes.map(note => <small className="qb-category-table-group-note" key={note}>{note}</small>)}</td> : null}
                {detail}<td className="number">{amount}{props.showPriceBasis && line.priceBasisDesc ? <details><summary>价格依据</summary><small>{line.priceBasisDesc}</small></details> : null}</td>
                {!flat && index === 0 ? <td rowSpan={rows.length} className="number qb-category-table-subtotal">{manualTableMoney(formatLineGroupTotalForDisplay(group.lines, props.showPriceBasis))}</td> : null}
                {flat || index === 0 ? <td rowSpan={flat ? undefined : rows.length} className="number qb-category-table-per-capita"><GroupPerCapita lines={flat ? [line] : group.lines} people={props.people} teacherPays={props.teacherPays} /></td> : null}
                {props.showActions ? <td className="qb-category-table-actions">{row.showActions ? props.renderActions(line, row.key, display) : null}</td> : null}
              </tr>;
            })}</tbody>;
          })}
        </table>
      </div>
    </section>;
  })}</>;
}
